import React, { useState } from 'react';
import { ArrowLeft, Music, ChevronDown, HelpCircle } from 'lucide-react';
import useSEO from '../hooks/useSEO';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);
  
  // SEO Meta Tags
  useSEO({
    title: 'FAQ - Questions Fréquentes | DJ Nomad',
    description: 'Trouvez les réponses à vos questions sur DJ Nomad : fonctionnement offline, QR code invités, tarifs, stockage cloud et plus encore.',
    canonical: 'https://djnomad.app/faq',
    ogTitle: 'FAQ DJ Nomad - Questions Fréquentes',
    ogDescription: 'Réponses aux questions fréquentes sur DJ Nomad.'
  });

  const faqs = [
    {
      category: "Général",
      questions: [
        {
          q: "Qu'est-ce que DJ Nomad ?",
          a: "DJ Nomad est une application web qui automatise la musique de vos événements sans avoir besoin d'un DJ. Elle vous permet de gérer vos playlists, recevoir des suggestions des invités via QR code, et surtout, de continuer à diffuser la musique même sans connexion Internet."
        },
        {
          q: "Est-ce vraiment gratuit ?",
          a: "Oui ! Le plan gratuit inclut 3 événements simultanés, 2 GB de stockage et 50 suggestions par événement. C'est suffisant pour la plupart des utilisateurs. Un plan Pro à 19€/mois est disponible pour les professionnels qui organisent de nombreux événements."
        },
        {
          q: "Ai-je besoin de compétences de DJ ?",
          a: "Absolument pas ! DJ Nomad est conçu pour les organisateurs d'événements, wedding planners, traiteurs et particuliers. Les transitions sont automatiques, aucune compétence technique n'est requise."
        }
      ]
    },
    {
      category: "Mode Offline",
      questions: [
        {
          q: "Comment fonctionne le mode offline ?",
          a: "C'est simple : 1) Créez votre événement et ajoutez vos MP3 (avec Internet), 2) Cliquez sur 'Préparer pour Offline' dans l'éditeur d'événement, 3) Attendez que tout soit téléchargé, 4) Partez en soirée ! L'app fonctionne sans Internet grâce aux technologies PWA."
        },
        {
          q: "Combien d'espace de stockage est nécessaire ?",
          a: "En moyenne : 1 chanson MP3 = 5-10 MB. Un événement de 50 chansons = ~250-500 MB. Le plan gratuit offre 2 GB de stockage cloud, soit environ 400 chansons."
        },
        {
          q: "Que se passe-t-il si je perds la connexion ?",
          a: "Si votre événement est préparé pour offline, DJ Nomad bascule automatiquement. Un badge 'Offline' apparaît, mais tout continue de fonctionner : lecture, transitions, playlists."
        }
      ]
    },
    {
      category: "QR Code & Suggestions",
      questions: [
        {
          q: "Comment fonctionne le QR code invités ?",
          a: "Chaque événement a un QR code unique. Les invités scannent le code, accèdent à une interface simple, et proposent des musiques. Vous recevez les suggestions en temps réel et décidez d'accepter ou refuser."
        },
        {
          q: "Combien de suggestions par événement ?",
          a: "Le plan gratuit permet 50 suggestions par événement. Le plan Pro offre des suggestions illimitées pour les grands événements."
        },
        {
          q: "Puis-je modérer les suggestions ?",
          a: "Oui ! Vous avez le contrôle total. Chaque suggestion peut être acceptée, refusée ou mise en attente. Vous restez maître de la playlist."
        }
      ]
    },
    {
      category: "Fonctionnalités",
      questions: [
        {
          q: "Quels formats audio sont supportés ?",
          a: "DJ Nomad supporte les formats les plus courants : MP3, WAV, OGG, M4A, FLAC. Pour les meilleures performances, nous recommandons le MP3 (320kbps)."
        },
        {
          q: "Comment fonctionnent les transitions automatiques ?",
          a: "DJ Nomad détecte automatiquement le BPM (tempo) de chaque morceau et effectue des crossfades fluides entre les chansons. C'est comme avoir un DJ automatique !"
        },
        {
          q: "Puis-je organiser par parties (cocktail, dîner, danse) ?",
          a: "Oui ! Vous pouvez créer des 'parties' dans votre événement, chacune avec ses propres playlists. Parfait pour gérer les différents moments de votre soirée."
        }
      ]
    },
    {
      category: "Technique",
      questions: [
        {
          q: "Sur quels navigateurs DJ Nomad fonctionne-t-il ?",
          a: "DJ Nomad fonctionne sur tous les navigateurs modernes : Chrome, Edge, Firefox, Safari (desktop et mobile). Chrome et Edge offrent les meilleures performances pour le mode offline."
        },
        {
          q: "Puis-je utiliser DJ Nomad sur mobile/tablette ?",
          a: "Oui ! DJ Nomad est une PWA (Progressive Web App), installable sur mobile et tablette. Mobile est idéal pour préparer et surveiller les événements, desktop pour l'interface complète."
        },
        {
          q: "Mes données sont-elles synchronisées entre appareils ?",
          a: "Oui ! Créez votre événement sur ordinateur, préparez-le offline sur tablette : tout se synchronise automatiquement quand vous êtes connecté."
        }
      ]
    },
    {
      category: "Sécurité & Confidentialité",
      questions: [
        {
          q: "Mes fichiers sont-ils sécurisés ?",
          a: "Oui. Vos MP3 sont chiffrés en transit (SSL/TLS) et stockés sur des serveurs sécurisés en Europe. Vous seul avez accès à vos fichiers."
        },
        {
          q: "Que faites-vous de mes données personnelles ?",
          a: "Nous collectons le strict minimum : email et événements que vous créez. Conformément au RGPD, vous pouvez accéder, modifier ou supprimer vos données à tout moment."
        },
        {
          q: "Respectez-vous les droits d'auteur ?",
          a: "DJ Nomad est un outil technique. Vous êtes responsable de vous assurer que vous avez les droits d'utiliser les musiques uploadées. Nous ne vendons pas de musique."
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-50 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <a
            href="/"
            className="flex items-center gap-2 text-gray-600 hover:text-violet-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour à l&apos;accueil</span>
          </a>
          <a href="/" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-violet-600 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
              <Music className="w-5 h-5 text-white" />
            </div>
            <span className="text-gray-900 font-bold text-lg">DJ Nomad</span>
          </a>
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-violet-600 via-purple-600 to-violet-700 py-16">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl mb-6">
            <HelpCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Questions Fréquentes</h1>
          <p className="text-xl text-white/80">Toutes les réponses à vos questions sur DJ Nomad</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-6 py-12">
        {/* FAQ Categories */}
        <div className="space-y-6">
          {faqs.map((category, catIndex) => (
            <div key={catIndex} className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-sm">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <div className="w-1.5 h-8 bg-gradient-to-b from-violet-600 to-purple-600 rounded-full" />
                {category.category}
              </h2>
              
              <div className="space-y-3">
                {category.questions.map((faq, faqIndex) => {
                  const globalIndex = `${catIndex}-${faqIndex}`;
                  const isOpen = openIndex === globalIndex;
                  
                  return (
                    <div
                      key={faqIndex}
                      className="border border-gray-200 rounded-xl overflow-hidden transition-all duration-300"
                    >
                      <button
                        onClick={() => setOpenIndex(isOpen ? null : globalIndex)}
                        className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-gray-900 font-semibold pr-4">{faq.q}</span>
                        <ChevronDown
                          className={`w-5 h-5 text-violet-600 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                        />
                      </button>
                      
                      <div
                        className={`transition-all duration-300 overflow-hidden ${
                          isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="px-5 pb-4 pt-2 text-gray-600 leading-relaxed border-t border-gray-100">
                          {faq.a}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Contact CTA */}
        <div className="mt-12 bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-2xl p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Vous ne trouvez pas votre réponse ?</h3>
          <p className="text-gray-600 mb-6">Notre équipe est là pour vous aider !</p>
          <a
            href="/contact"
            className="inline-block px-8 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white font-semibold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            Contactez-nous
          </a>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <p className="text-gray-400">© 2025 DJ Nomad. Tous droits réservés.</p>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
